import getParentFolderId from '@gaia/utils/getParentFolderId';
import find from 'lodash/find';
import mapValues from 'lodash/mapValues';
import memoize from 'lodash/memoize';
import omit from 'lodash/omit';

const isInFolder = (item: Area | Waypoint | Track | Folder, folderId: string) => {
    const parentFolderId = getParentFolderId(item);
    return parentFolderId === folderId;
};

function isFolder(object: Waypoint | Track | Area | Folder): object is Folder {
    return 'type' in object && object.type === 'folder';
}

type Store =
    | Record<string, Waypoint>
    | Record<string, Track>
    | Record<string, Area>
    | Record<string, Folder>;

// Return the properties of any filed objects that are already in local state
const getLocalObjectsInFolder = (store: Store, id: string) => {
    return Object.values(store)
        .filter((o) => isInFolder(o, id))
        .map((o) => {
            if (isFolder(o)) return o;
            return o.properties;
        });
};

// Return only the remote items that do not exist in local state
export function getNonLocalObjectsInFolder<T extends {id?: string}>(
    store: Store,
    children: T[] | undefined
): T[] {
    if (!children) return [];
    return children.filter(
        (o) =>
            o.id &&
            !find(store, (item) => {
                if (isFolder(item)) return item.id === o.id;
                return item.properties.id === o.id;
            })
    );
}

// Assign local items to folders as appropriate and convert them to a SavedItem
function getFolderMemberships(
    folders: Record<string, Folder>,
    areas: Record<string, Area>,
    waypoints: Record<string, Waypoint>,
    tracks: Record<string, Track>
): Record<string, Folder> {
    return mapValues(folders, (folder) => {
        const id = folder.id || folder.localId;
        // We need to deduplicate items that came on the folder and those that are already local
        const localTracks = getLocalObjectsInFolder(tracks, id);
        return {
            ...folder,
            areas: [
                ...getLocalObjectsInFolder(areas, id),
                ...getNonLocalObjectsInFolder(areas, folder.areas),
            ],
            waypoints: [
                ...getLocalObjectsInFolder(waypoints, id),
                ...getNonLocalObjectsInFolder(waypoints, folder.waypoints),
            ],
            tracks: [
                ...localTracks.filter(({track_type}) => !track_type),
                ...getNonLocalObjectsInFolder(tracks, folder.tracks),
            ],
            routes: [
                ...localTracks.filter(({track_type}) => track_type === 'route'),
                ...getNonLocalObjectsInFolder(tracks, folder.routes),
            ],
            folders: [
                ...Object.values(folders)
                    .filter((a) => isInFolder(a, id))
                    .map((a) => ({
                        ...omit(a, 'localId'),
                        id: a.id || a.localId,
                    })),
                ...getNonLocalObjectsInFolder(folders, folder.folders),
            ],
        };
    });
}

export default memoize(getFolderMemberships, (folders, areas, waypoints, tracks) =>
    JSON.stringify([folders, areas, waypoints, tracks])
);
